import { Badge, Button, Card, CloseButton, Flex, Group, TextInput } from "@mantine/core";
import { IconCircle, IconCircleCheck } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { ISSUE_DATAFILEDS_ENUM } from "../../issueConstants";
import {
  selectIssueRegisterNameOptionOnBoard,
  selectNrOfFiltredIssues,
  selectTaskRegisterNameOptionOnBoard,
} from "../../issueSelectors";
import {
  setIssueDropdownSingleDataFieldFilterInRedux,
  setMiniSearchFreeTextInRedux,
  setTaskAndOrIssueArgsInRedux,
} from "../../issueSlice";
import { FilterDropdown } from "./FilterDropdown";
import {
  makeSelectActiveFiltersByField,
  makeSelectUniqueIssueFilterFromDataField,
  selectAssignedToHashmap,
  selectDisciplineFilter,
  selectIssueDatesFiler,
  selectMineSakerFilter,
} from "./issueFilterSelectors";

const priortyMap = {
  LOW: "Lav",
  MEDIUM: "Medium",
  HIGH: "Høy",
};

export const IssueDbFilterBarNew: React.FC = () => {
  const dispatch = useAppDispatch();

  // Memoized selectors
  const selectFilters = useMemo(makeSelectUniqueIssueFilterFromDataField, []);
  const selectActive = useMemo(makeSelectActiveFiltersByField, []);

  // Hämta alla filter
  const tagsFilters = useAppSelector((state) => selectFilters(state, ISSUE_DATAFILEDS_ENUM.TAGS, null));
  const priorityFilters = useAppSelector((state) => selectFilters(state, ISSUE_DATAFILEDS_ENUM.PRIORITY, priortyMap));
  const statusFilters = useAppSelector((state) => selectFilters(state, ISSUE_DATAFILEDS_ENUM.STATUS, null));
  const typeFilters = useAppSelector((state) => selectFilters(state, ISSUE_DATAFILEDS_ENUM.TYPEOFISSUE, null));
  const bucketFilters = useAppSelector((state) => selectFilters(state, ISSUE_DATAFILEDS_ENUM.BUCKET, null));
  const disciplineFilter = useSelector(selectDisciplineFilter);
  const assignedToHashmap = useAppSelector(selectAssignedToHashmap);
  const assignedToFilters = useAppSelector((state) =>
    selectFilters(state, ISSUE_DATAFILEDS_ENUM.ASSIGNED_TO_ID, assignedToHashmap),
  );
  const mineSakerFilter = useAppSelector(selectMineSakerFilter);
  const dueDateFilter = useAppSelector(selectIssueDatesFiler);

  // Aktiva filter
  const activeTags = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.TAGS));
  const activePriority = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.PRIORITY));
  const activeStatus = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.STATUS));
  const activeAssignedTo = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.ASSIGNED_TO_ID));
  const activeDueDate = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.DATES_DUE));
  const activeDiscipline = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.DISCIPLIN));
  const activeType = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.TYPEOFISSUE));
  const activeBucket = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.BUCKET));
  const activeMineSaker = useAppSelector((s) => selectActive(s, ISSUE_DATAFILEDS_ENUM.MINE_SAKER));

  const nrOfFilteredIssues = useAppSelector(selectNrOfFiltredIssues);
  const miniSearchFreeText = useAppSelector((s) => s.issueReducerNew.miniSearchFreeText);

  // === FILTER KONFIGURATIONER ===
  const filterConfigs = useMemo(
    () =>
      [
        {
          filter: mineSakerFilter,
          active: activeMineSaker,
          field: ISSUE_DATAFILEDS_ENUM.MINE_SAKER,
        },
        {
          filter: statusFilters,
          active: activeStatus,
          field: ISSUE_DATAFILEDS_ENUM.STATUS,
        },
        {
          filter: dueDateFilter,
          active: activeDueDate,
          field: ISSUE_DATAFILEDS_ENUM.DATES_DUE,
        },
        {
          filter: bucketFilters,
          active: activeBucket,
          field: ISSUE_DATAFILEDS_ENUM.BUCKET,
        },
        {
          filter: tagsFilters,
          active: activeTags,
          field: ISSUE_DATAFILEDS_ENUM.TAGS,
        },
        {
          filter: typeFilters,
          active: activeType,
          field: ISSUE_DATAFILEDS_ENUM.TYPEOFISSUE,
        },
        {
          filter: assignedToFilters,
          active: activeAssignedTo,
          field: ISSUE_DATAFILEDS_ENUM.ASSIGNED_TO_ID,
        },
        {
          filter: disciplineFilter,
          active: activeDiscipline,
          field: ISSUE_DATAFILEDS_ENUM.DISCIPLIN,
        },
        {
          filter: priorityFilters,
          active: activePriority,
          field: ISSUE_DATAFILEDS_ENUM.PRIORITY,
        },
      ].filter((config) => config.filter?.options != null),
    [
      mineSakerFilter,
      tagsFilters,
      priorityFilters,
      dueDateFilter,
      statusFilters,
      assignedToFilters,
      disciplineFilter,
      typeFilters,
      bucketFilters,
      activeMineSaker,
      activeTags,
      activePriority,
      activeDueDate,
      activeStatus,
      activeAssignedTo,
      activeDiscipline,
      activeType,
      activeBucket,
    ],
  );

  const handleFilterChange = useCallback(
    (filterKey: ISSUE_DATAFILEDS_ENUM, values: string[]) => {
      dispatch(
        setIssueDropdownSingleDataFieldFilterInRedux({
          filter: filterKey,
          values,
        }),
      );
    },
    [dispatch],
  );

  return (
    <Card mx="xxs" p="xs" withBorder radius="sm" bg={"#fafafb"} bd={"1px solid var(--mantine-color-warmgray-2)"}>
      <Group justify="space-between" wrap="nowrap" gap="0">
        <Flex rowGap="xxs" columnGap={"0.7em"} wrap={"wrap"} align="center">
          <TaskIssueToggle />
          <TextInput
            placeholder="Søk..."
            value={miniSearchFreeText}
            onChange={(e) => dispatch(setMiniSearchFreeTextInRedux(e.currentTarget.value))}
            size="sm"
            w={150}
            miw={120}
            rightSection={
              miniSearchFreeText?.length > 0 && (
                <CloseButton size="sm" onClick={() => dispatch(setMiniSearchFreeTextInRedux(""))} title="Tøm søk" />
              )
            }
          />

          {/* LOOPA ÖVER ALLA FILTER */}
          {filterConfigs.map(({ filter, active, field }) => (
            <FilterDropdown
              key={field}
              filter={filter}
              value={active}
              onChange={(key, values) => handleFilterChange(key as ISSUE_DATAFILEDS_ENUM, values)}
              enableSearch
            />
          ))}
        </Flex>
        <Group miw={"40px"} justify="end" align="start" h="100%" pt={"6px"}>
          <Badge color="green" size="sm" variant="filled">
            {nrOfFilteredIssues}
          </Badge>
        </Group>
      </Group>
    </Card>
  );
};

// TaskIssueToggle förblir oförändrad
export const TaskIssueToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const selected = useAppSelector((s) => s.issueReducerNew.taskAndOrIssueArgs);

  const isTask = selected.includes("TASK");
  const isIssue = selected.includes("ISSUE");

  const update = useCallback(
    (type: "TASK" | "ISSUE") => {
      let next: ("TASK" | "ISSUE")[];
      if (selected.includes(type)) {
        next = selected.filter((t) => t !== type);
      } else {
        next = [...selected, type];
      }
      if (next.length === 0) {
        next = ["TASK", "ISSUE"];
      }
      dispatch(setTaskAndOrIssueArgsInRedux(next));
    },
    [selected, dispatch],
  );

  const issueNameOption = useAppSelector(selectIssueRegisterNameOptionOnBoard);
  const taskNameOption = useAppSelector(selectTaskRegisterNameOptionOnBoard);

  return (
    <Group gap="2px" wrap="nowrap">
      <Button
        style={{ borderRadius: "6px 0 0 6px" }}
        size="sm"
        px="xs"
        color={isIssue ? "mc-primary" : "warmgray.6"}
        variant={isIssue ? "filled" : "light"}
        onClick={() => update("ISSUE")}
        rightSection={isIssue ? <IconCircleCheck size={18} /> : <IconCircle size={18} />}
      >
        {issueNameOption ? issueNameOption.singular : "Sak"}
      </Button>
      <Button
        style={{ borderRadius: "0 6px 6px 0" }}
        size="sm"
        px="xs"
        color={isTask ? "mc-primary" : "warmgray.6"}
        variant={isTask ? "filled" : "light"}
        onClick={() => update("TASK")}
        rightSection={isTask ? <IconCircleCheck size={18} /> : <IconCircle size={18} />}
      >
        {taskNameOption ? taskNameOption.singular : "Aksjon"}
      </Button>
    </Group>
  );
};
